import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Download, Upload, Pencil, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { payrollTakeOn } from '../../api';
import { currency } from '../billing/shared';
import { TaxYearSelect } from './TaxTabs';
import { TakeOnDialog } from './TakeOnDialog';

const thisTaxYear = () => { const d = new Date(); return d.getMonth() >= 2 ? d.getFullYear() + 1 : d.getFullYear(); };
const monthLabel = (m) => new Date(`${m}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
const FIELDS = [['paye', 'PAYE'], ['eti', 'ETI used'], ['sdl', 'SDL'], ['uif', 'UIF (ee+er)']];

const PriorEmp201 = ({ clientId, year, rows, onSaved, toast }) => {
  const [grid, setGrid] = useState(rows);
  const [busy, setBusy] = useState(false);
  useEffect(() => setGrid(rows), [rows]);
  const dirty = JSON.stringify(grid) !== JSON.stringify(rows);
  const save = () => {
    setBusy(true);
    payrollTakeOn.saveEmp201(clientId, year, grid.map((r) => ({ month: r.month, ...Object.fromEntries(FIELDS.map(([k]) => [k, Number(r[k]) || 0])) })))
      .then((d) => { toast({ title: 'Previous EMP201s saved' }); onSaved(d); })
      .catch((e) => toast({ title: 'Save failed', description: e?.response?.data?.detail?.toString() || 'Try again.', variant: 'destructive' }))
      .finally(() => setBusy(false));
  };
  return (
    <div className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="takeon-emp201">
      <div className="p-5 border-b border-stone-100 flex items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-stone-900">EMP201s submitted by the previous payroll</div>
          <div className="text-xs text-stone-500 mt-0.5">Only fill in months paid before you took over. They are added to the EMP201 and EMP501 tabs.</div>
        </div>
        <Button size="sm" disabled={!dirty || busy} onClick={save} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="takeon-emp201-save-btn">{busy ? <Loader2 size={14} className="mr-1.5 animate-spin"/> : <Save size={14} className="mr-1.5"/>} Save</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider"><tr><th className="text-left px-4 py-2">Month</th>{FIELDS.map(([, l]) => <th key={l} className="text-right px-4 py-2">{l}</th>)}</tr></thead>
          <tbody className="divide-y divide-stone-100">
            {grid.map((r, i) => (
              <tr key={r.month} data-testid={`takeon-emp201-row-${r.month}`}>
                <td className="px-4 py-1.5 text-stone-700">{monthLabel(r.month)}</td>
                {FIELDS.map(([k]) => (
                  <td key={k} className="px-4 py-1.5 text-right">
                    <input type="number" min="0" step="0.01" value={r[k] || ''} onChange={(e) => setGrid(grid.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))}
                      className="w-28 h-8 rounded-md border border-stone-200 px-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-emerald-600" data-testid={`takeon-emp201-${k}-${r.month}`}/>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const TakeOnTab = ({ clientId, toast }) => {
  const [year, setYear] = useState(thisTaxYear());
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const load = () => payrollTakeOn.get(clientId, year).then(setData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setData(null); load(); }, [clientId, year]);

  const upload = (file) => {
    setImporting(true);
    payrollTakeOn.importCsv(clientId, year, file).then((r) => {
      toast({ title: `Imported ${r.imported} employee${r.imported === 1 ? '' : 's'}`, description: r.errors.length ? r.errors.slice(0, 3).join(' · ') : undefined, variant: r.errors.length && !r.imported ? 'destructive' : undefined });
      load();
    }).catch((e) => toast({ title: 'Import failed', description: e?.response?.data?.detail || 'Check the file.', variant: 'destructive' })).finally(() => setImporting(false));
  };

  return (
    <div className="space-y-6" data-testid="payroll-takeon-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <TaxYearSelect value={year} onChange={setYear} testid="takeon-year-select"/>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" asChild data-testid="takeon-template-btn"><a href={payrollTakeOn.templateUrl(clientId, year)}><Download size={14} className="mr-1.5"/> CSV template</a></Button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { if (e.target.files?.[0]) upload(e.target.files[0]); e.target.value = ''; }} data-testid="takeon-import-input"/>
          <Button size="sm" disabled={importing} onClick={() => fileRef.current?.click()} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="takeon-import-btn">{importing ? <Loader2 size={14} className="mr-1.5 animate-spin"/> : <Upload size={14} className="mr-1.5"/>} Upload CSV</Button>
        </div>
      </div>
      {!data ? <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-stone-400"/></div> : (
        <>
          <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
            <div className="p-5 border-b border-stone-100">
              <div className="font-semibold text-stone-900">Employee take-on balances</div>
              <div className="text-xs text-stone-500 mt-0.5">Year-to-date figures from the previous payroll for clients you took over mid-year.</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="takeon-table">
                <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
                  <tr>{['Employee', 'Up to', 'Periods', 'Income (3601)', 'PAYE (4102)', 'UIF (4141)', ''].map((h, i) => <th key={h || i} className={`px-4 py-3 font-medium ${i >= 2 && i <= 5 ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {data.employees.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-stone-500">Add employees first.</td></tr>}
                  {data.employees.map((e) => {
                    const t = e.takeOn;
                    return (
                      <tr key={e.id} className={e.active ? '' : 'opacity-50'} data-testid={`takeon-row-${e.id}`}>
                        <td className="px-4 py-3 font-medium text-stone-900">{e.name}<div className="text-[11px] font-normal text-stone-500">{e.employeeNumber}</div></td>
                        <td className="px-4 py-3 text-stone-600">{t?.upToDate || <span className="text-stone-400">None</span>}</td>
                        <td className="px-4 py-3 text-right">{t ? t.periodsPaid : '—'}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{t ? currency(t.codes['3601'] || 0) : '—'}</td>
                        <td className="px-4 py-3 text-right tabular-nums" data-testid={`takeon-paye-${e.id}`}>{t ? currency(t.codes['4102'] || 0) : '—'}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{t ? currency(t.codes['4141'] || 0) : '—'}</td>
                        <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => setEditing(e)} data-testid={`takeon-edit-${e.id}`}><Pencil size={13} className="mr-1"/> {t ? 'Edit' : 'Add'}</Button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <PriorEmp201 clientId={clientId} year={year} rows={data.emp201} onSaved={setData} toast={toast}/>
        </>
      )}
      <TakeOnDialog employee={editing} taxYear={year} codes={data?.codes || []} onOpenChange={(o) => { if (!o) setEditing(null); }} onSaved={load} toast={toast}/>
    </div>
  );
};
